import React from 'react';
import { Link } from 'react-router-dom';
import { Text } from '@atlaskit/primitives';
import { trackLinkClick } from '@atlassian/temp-analytics';
import EntitlementIcon from '@css-xp/base/entitlement-icon';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { ColGroup, TableBody, TableHead, CustomDynamicResultTable, FormattedDate } from '@css-xp/base';
import { TransactionAccountProduct, ProvisionedResourceType } from '@css-xp/graphtypes/src/generated-types-new';
import { NoData } from '@css-xp/ppc-common';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { routePaths, SUBSCRIPTION_CONSTANTS } from '@css-xp/ppc-constants';
import { renderSubscriptionWidgetTimestamp } from './subscription-widget-items';
import './widget-items.scss';
import './subscription-widget-table.scss';

const headersMap = {
  [SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION]: {
    name: SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION,
    content: SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION,
    customClassNames: 'col-subscription',
  },
  [SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING]: {
    name: SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING,
    content: SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING,
    customClassNames: 'col-plan',
  },
  [SUBSCRIPTION_CONSTANTS.COLUMNS.SITE]: {
    name: SUBSCRIPTION_CONSTANTS.COLUMNS.SITE,
    content: SUBSCRIPTION_CONSTANTS.COLUMNS.SITE,
    customClassNames: 'col-site',
  },
  [SUBSCRIPTION_CONSTANTS.COLUMNS.CREATED]: {
    name: SUBSCRIPTION_CONSTANTS.COLUMNS.CREATED,
    content: SUBSCRIPTION_CONSTANTS.COLUMNS.CREATED,
    customClassNames: 'col-common',
  },
  [SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_IN]: {
    name: SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_IN,
    content: SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_IN,
    customClassNames: 'col-common',
  },
};

const getHeaders = (isRenewSoonInterface?: boolean) => {
  const allColumns = [
    headersMap[SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION],
    headersMap[SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING],
    headersMap[SUBSCRIPTION_CONSTANTS.COLUMNS.SITE],
    isRenewSoonInterface
      ? headersMap[SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_IN]
      : headersMap[SUBSCRIPTION_CONSTANTS.COLUMNS.CREATED],
  ];

  return allColumns;
};

type SubscriptionWidgetTableType = {
  subscriptions: Partial<TransactionAccountProduct | null>[];
  heading?: string;
  // To improve: Instead of isRenewSoonInterface, we can improve the logic using columnsToShow prop
  isRenewSoonInterface?: boolean;
};
/**
 * Component for Subscription Widget table on the Home page
 * @constructor
 */
const SubscriptionWidgetTableComponent = ({
  heading = '',
  subscriptions,
  isRenewSoonInterface,
}: SubscriptionWidgetTableType) => {
  return (
    <CustomDynamicResultTable
      className="mt-0 subscription-widget-table"
      noResults={false} // TODO: To be completed in ITFO-24783
      isLoading={false} // TODO: To be completed in ITFO-24783
      error={undefined} // TODO: To be completed in ITFO-24783
      emptyStateProps={{ header: 'No data' }} // TODO: To be completed in ITFO-24783
    >
      <ColGroup columns={getHeaders(isRenewSoonInterface)} />
      <TableHead columns={getHeaders(isRenewSoonInterface)} />
      <TableBody>
        {subscriptions?.map((item: any) => {
          if (!item || !item.transactionAccountId) {
            return null;
          }

          const URL = getRouteUrl(routePaths.entitlement, {
            txaId: item.transactionAccountId,
            entitlementId: item.id,
          });
          const shouldShowOrgInfo = item.orgProvisionedResourceType === ProvisionedResourceType.ORGANISATION;
          const renderCloudSite = () => {
            if (shouldShowOrgInfo) {
              // Since the header name is cloud site, we are not showing the org name
              return <NoData />;
            }
            return item.cloudSiteHostname ?? <NoData />;
          };

          return (
            <tr key={item.entitlementNumber}>
              <td>
                <Link
                  to={URL}
                  onClick={() => {
                    trackLinkClick('widgetItemClick', { source: heading });
                  }}
                >
                  <div className="flex align-center">
                    <EntitlementIcon
                      productId={item.productId ?? ''}
                      isCloud={true}
                      className="avatar medium entitlement-icon"
                      logoId={item.logoId ?? ''}
                    />
                    <div className="text-ellipsis subscription-cell-text">
                      <p className="text-ellipsis">{item.productName}</p>
                      <Text color="color.text.subtlest" size="small">
                        {item.entitlementNumber}
                      </Text>
                    </div>
                  </div>
                </Link>
              </td>
              <td>{item?.offering?.name ?? item?.edition ?? <NoData testId="no-plan-data" />}</td>
              <td>{renderCloudSite()}</td>
              <td>
                {isRenewSoonInterface ? (
                  renderSubscriptionWidgetTimestamp(item.latestPeriodEndDate, 'Renew today', '', true)
                ) : (
                  <FormattedDate date={item.createdDate} format={NEW_DATE_FORMAT} />
                )}
              </td>
            </tr>
          );
        })}
      </TableBody>
    </CustomDynamicResultTable>
  );
};

export const SubscriptionWidgetTable = React.memo(SubscriptionWidgetTableComponent);
